import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Check, X, Keyboard, ListChecks, Sparkles, BookOpen,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_BADGE = {
  fresh:  'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  fading: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  stale:  'bg-rose-500/20 text-rose-300 border-rose-500/30',
};

// Normalise user input for "typing" mode answer checking.
const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

export default function AdminFlashcardsStudy() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState('recognition'); // 'recognition' | 'typing'
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [queue, setQueue] = useState([]);          // cards remaining
  const [done, setDone] = useState(0);
  const [right, setRight] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [feedback, setFeedback] = useState(null);  // {correct, expected}
  const [typed, setTyped] = useState('');
  const [pickedOption, setPickedOption] = useState(null);
  const inputRef = useRef(null);

  // Re-insert wrong cards 3 positions later (BYKI-style).
  const REINSERT_AFTER = 3;

  const startSession = async (chosenMode = mode) => {
    setLoading(true);
    setSession(null);
    setQueue([]);
    setDone(0);
    setRight(0);
    setWrong(0);
    setFeedback(null);
    setTyped('');
    setPickedOption(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/forge-vocab/lists/${listId}/session`,
        null,
        { params: { mode: chosenMode }, withCredentials: true },
      );
      setSession(res.data);
      setQueue(res.data.cards || []);
      setMode(chosenMode);
    } catch (err) {
      toast.error(`Failed to start session: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { startSession('recognition'); }, [listId]); // eslint-disable-line

  const current = queue[0] || null;
  const totalCards = (session?.total || 0) + wrong; // wrong cards re-add to the queue

  const handleAnswer = async (isCorrect, _expectedOverride = null) => {
    if (!current || feedback) return;
    setFeedback({ correct: isCorrect, expected: current.correct_answer });
    if (isCorrect) setRight((n) => n + 1);
    else setWrong((n) => n + 1);
    try {
      await axios.post(`${BACKEND_URL}/api/forge-vocab/answer`, {
        item_id: current.item_id,
        mode,
        correct: !!isCorrect,
      }, { withCredentials: true });
    } catch (err) {
      console.warn('answer submit failed', err);
    }
  };

  const next = () => {
    if (!current) return;
    const wasWrong = feedback && !feedback.correct;
    setQueue((q) => {
      const [head, ...rest] = q;
      if (wasWrong) {
        // Re-insert head after N cards.
        const reinsertAt = Math.min(REINSERT_AFTER, rest.length);
        const newRest = [...rest.slice(0, reinsertAt), head, ...rest.slice(reinsertAt)];
        return newRest;
      }
      return rest;
    });
    setDone((n) => n + 1);
    setFeedback(null);
    setTyped('');
    setPickedOption(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const submitTyped = () => {
    if (!current || feedback) return;
    const ok = norm(typed) === norm(current.correct_answer);
    handleAnswer(ok);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (feedback) next();
      else if (mode === 'typing') submitTyped();
    }
  };

  const progressPct = useMemo(() => {
    const total = totalCards || 1;
    return Math.min(100, Math.round((done / total) * 100));
  }, [done, totalCards]);

  const finished = !current && session && (done > 0 || (session.cards || []).length === 0);

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl" data-testid="study-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/flashcards')} data-testid="study-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Lists
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant={mode === 'recognition' ? 'default' : 'outline'}
            size="sm"
            onClick={() => startSession('recognition')}
            data-testid="mode-recognition"
          >
            <ListChecks className="h-4 w-4 mr-1" /> Recognition
          </Button>
          <Button
            variant={mode === 'typing' ? 'default' : 'outline'}
            size="sm"
            onClick={() => startSession('typing')}
            data-testid="mode-typing"
          >
            <Keyboard className="h-4 w-4 mr-1" /> Typing
          </Button>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>{done} of {totalCards || 0} · ✓ {right} · ✗ {wrong}</span>
          <span>{session?.language_code?.toUpperCase()} · {mode}</span>
        </div>
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-cyan-500 transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Preparing session…
        </div>
      ) : finished ? (
        <Card data-testid="study-finished">
          <CardContent className="pt-8 pb-8 text-center space-y-3">
            <Sparkles className="h-10 w-10 mx-auto text-amber-400" />
            <h2 className="text-2xl font-semibold">Session complete</h2>
            <p className="text-muted-foreground text-sm">
              {right} correct · {wrong} re-tries · {done} cards total
            </p>
            <div className="flex gap-2 justify-center pt-2">
              <Button onClick={() => startSession(mode)}>Restart</Button>
              <Button variant="outline" onClick={() => navigate('/admin/flashcards')}>Done</Button>
            </div>
          </CardContent>
        </Card>
      ) : !current ? (
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
            <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No cards in this list yet.
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="study-card" onKeyDown={onKeyDown}>
          <CardContent className="pt-6 pb-6">
            <div className="flex items-center justify-between mb-3 text-xs">
              <Badge variant="outline" className={STATE_BADGE[current.state] || ''}>
                {current.state}
              </Badge>
              <span className="text-muted-foreground">score {current.score}</span>
            </div>

            <div className="text-4xl font-semibold text-center mb-1" data-testid="card-word">
              {current.word}
            </div>
            {current.ipa && (
              <div className="text-center text-sm text-muted-foreground mb-4">/{current.ipa}/</div>
            )}

            {/* Recognition: 4 MC options */}
            {mode === 'recognition' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {(current.options || []).map((opt) => {
                  let cls = 'border-border bg-background hover:bg-muted';
                  if (feedback) {
                    if (opt === current.correct_answer) cls = 'border-emerald-500 bg-emerald-500/15';
                    else if (opt === pickedOption) cls = 'border-rose-500 bg-rose-500/15';
                    else cls = 'border-border bg-background opacity-60';
                  } else if (opt === pickedOption) {
                    cls = 'border-cyan-500 bg-cyan-500/10';
                  }
                  return (
                    <button
                      key={opt}
                      onClick={() => {
                        if (feedback) return;
                        setPickedOption(opt);
                        handleAnswer(opt === current.correct_answer);
                      }}
                      className={`text-left px-4 py-3 rounded-lg border text-sm transition ${cls}`}
                      data-testid={`option-${opt}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Typing */}
            {mode === 'typing' && (
              <div className="space-y-2 mb-4">
                <Input
                  ref={inputRef}
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder="Type the English meaning…"
                  disabled={!!feedback}
                  data-testid="typing-input"
                />
                {!feedback && (
                  <Button onClick={submitTyped} className="w-full" data-testid="submit-typed">
                    Check
                  </Button>
                )}
              </div>
            )}

            {/* Feedback panel */}
            {feedback && (
              <div className={`rounded-lg p-3 mb-3 text-sm ${
                feedback.correct
                  ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300'
                  : 'bg-rose-500/10 border border-rose-500/30 text-rose-200'
              }`} data-testid="feedback-panel">
                <div className="flex items-center gap-2 font-medium">
                  {feedback.correct ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                  {feedback.correct ? 'Correct' : `Answer: ${feedback.expected}`}
                </div>
                {current.sentences?.length > 0 && (
                  <div className="mt-2 text-xs opacity-90 space-y-1">
                    {current.sentences.slice(0, 2).map((s, i) => (
                      <div key={i}>· {s}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {feedback && (
              <Button onClick={next} className="w-full" data-testid="next-card">
                Next  (Enter)
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
